import API from '@api';
import LoadingSkeleton from '@components/LoadingSkeleton';
import { showPriceChangeWarningNotification } from '@components/PriceChangeWarning/notification';
import {
  COURIER_DELIVERY_ID,
  EXPRESS_DELIVERY_INTERVAL_ID,
  PICKUP_DELIVERY_ID,
  expressDeliveryStorage
} from '@constants/delivery';
import { MOSCOW_DEFAULT_STORE_ID } from '@constants/regions';
import useOutsideAlerter from '@customHooks/useOutsideAlerter';
import useWindowSize from '@customHooks/useWindowSize';
import { Picture, Tooltip } from '@ui';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import type { MouseEvent } from 'react';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { createAddressString } from '@utils/address';
import HeaderModalAddress from '../HeaderAddressModal';
import ModalMapfunc from '../ModalMapfunc';
import RegionModal from '@components/Modals/RegionModal';
import { useStores } from '@root/src/stores';
import icons from '@icons';
import { expressPointsNames } from './constants';
import { createDeliveryTitle, isExpressDeliveryHeading } from './utils';
import useStorePolling from './hooks/useStorePolling';
import usePickupPoints from './hooks/usePickupPoints';
import useAddressOptionsPopup from './hooks/useAddressOptionsPopup';
import './index.scss';

const {
  store: { white: storeWhiteFilled },
  carItalic: { white: carItalicWhiteFilled },
  rocket: { white: rocketWhiteFilled }
} = icons.Fill;

const {
  chevronDown: { white: chevronDownWhiteOutlined }
} = icons.Outline;

const NO_ADDRESS_TITLE = 'Указать адрес доставки';

const LazyExpressDeliveryInfoModal = lazy(
  async () => await import('../ExpressDeliveryInfoModal')
);

const HeaderDropdown = observer(() => {
  const { cartStore, userStore } = useStores();
  const { isUserLoggedIn } = userStore;
  const { isLoadCart, openedShops } = cartStore;
  const { windowWidth } = useWindowSize();
  const [isShow, setIsShow] = useState(false);

  const items = useStorePolling();

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useOutsideAlerter({
    ref: dropdownRef,
    callback: () => {
      isShow && setIsShow(false);
    },
    withScroll: false,
    excludeSelector: '#popup-root'
  });

  const [isShowPickups, setIsShowPickups] = useState(false);
  const [isChangeAddress, setIsChangeAddress] = useState(false);
  const [initialState, setInitialState] = useState<any>(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    openPopupId,
    showOptionsPopup,
    togglePopup,
    addressOptionsPopupRef,
    addressItemRefs
  } = useAddressOptionsPopup();

  const [nearestDelivery, setNearestDelivery] = useState<any[]>([]);
  const pickupPointsList = usePickupPoints(cartStore);
  const [isShowPPModal, setIsShowPPModal] = useState(false);
  const [isShowGuestMap, setIsShowGuestMap] = useState(false);
  const [guestAddressModalKey, setGuestAddressModalKey] = useState(0);

  const [defState, setDefState] = useState<any>(false);

  const { store_id, dadata, deliveryIntervalId } = cartStore.cart;

  const dadataID = dadata?.id;
  const selectedDaData = dadata?.id;
  const hasDeliveryAddress =
    !!selectedDaData && !cartStore.isCartAddressFromOtherRegion;
  const isExpressHeading = isExpressDeliveryHeading({
    isExpressSelected:
      expressDeliveryStorage.get() ||
      deliveryIntervalId === EXPRESS_DELIVERY_INTERVAL_ID,
    hasAddress: hasDeliveryAddress
  });

  const active = items.findIndex(item => item.id === store_id) || 0;

  const selectAddress = useCallback(
    (item: any) => {
      if (!item?.id) return;

      const { updateCartStage } = cartStore;
      const { id } = item;

      const data = {
        storeId: id,
        deliveryId: id === 1 ? 1 : 5
      };

      API.updatePeopleStoreId()
        .then(async () => await updateCartStage(data))
        .then(() => {
          cartStore.setSelectExpressDel(false);
          expressDeliveryStorage.set(true);
        });
    },
    [cartStore]
  );

  const onSelectAddress = useCallback(
    (address: any) =>
      (isShouldClose: any = true) => {
        if (!isLoading) {
          setIsLoading(true);
          cartStore
            .updateCartStage({
              peopleAddressId: address.id,
              // storeId: cartStore?.regionData?.default_store_id ?? 1,
              deliveryId: 1
            })
            .then(() => {
              expressDeliveryStorage.set(false);
              setIsLoading(false);
              if (isShouldClose) {
                setIsShow(false);
              }
            });
        }
      },
    [cartStore, isLoading]
  );

  const selectPP = useCallback(
    (address: any) =>
      (isShouldClose: any = true) => {
        if (!isLoading) {
          setIsLoading(true);

          const pickup = pickupPointsList?.find(
            item => Number(item.id) === Number(address.id)
          );

          cartStore
            .selectPickupStore(
              address.id,
              address.city_id ?? pickup?.city_id,
              address.id === MOSCOW_DEFAULT_STORE_ID
                ? COURIER_DELIVERY_ID
                : PICKUP_DELIVERY_ID
            )
            .then(() => {
              expressDeliveryStorage.set(false);
              setIsLoading(false);
              if (isShouldClose) {
                setIsShow(false);
              }
            });
        }
      },
    [cartStore, isLoading, pickupPointsList]
  );

  const triggerChangePickupStore = (id: number): void => {
    const pickup = pickupPointsList?.find(
      item => Number(item.id) === Number(id)
    );
    expressDeliveryStorage.set(false);
    void cartStore.selectPickupStore(id, pickup?.city_id);
  };

  const deleteAddress = useCallback(
    (e: MouseEvent, item: any) => {
      e.stopPropagation();
      userStore.removeAddress(item?.id).then(() => {
        // console.log('res', res)
      });
    },
    [userStore]
  );

  const changeEditAddress = useCallback((defaultState?: any) => {
    setIsShow(false);
    setIsChangeAddress(true);
    setDefState(defaultState || false);
  }, []);

  const changeStateModalEdit = useCallback(
    (address: any, e: MouseEvent) => {
      e.stopPropagation();
      setInitialState(address);
      changeEditAddress();
    },
    [changeEditAddress]
  );

  const testfunc = useCallback(
    (isSelectExpressDel: any) => {
      const item =
        items.find(
          item =>
            isSelectExpressDel === item.id &&
            expressPointsNames.includes(item?.type || '')
        ) || items.find(item => expressPointsNames.includes(item?.type || ''));
      selectAddress(item);
    },
    [items, selectAddress]
  );

  useEffect(() => {
    const disposer = reaction(
      () => cartStore?.isSelectExpressDel,
      isSelectExpressDel => {
        if (isSelectExpressDel) {
          testfunc(isSelectExpressDel);
        }
      }
    );
    return () => {
      disposer();
    };
  }, [cartStore?.isSelectExpressDel, items.length, testfunc]);

  useEffect(() => {
    cartStore.getNearestDeliveryTypes().then((res: any) => {
      setNearestDelivery(res.nearestDelivery ?? []);
    });
  }, [active, dadataID, cartStore]);

  const hide = useCallback(() => {
    setIsShow(false);
    setIsChangeAddress(false);
    setInitialState(false);
    setDefState(false);
    setIsShowPPModal(false);
    setIsShowGuestMap(false);
  }, []);

  const openGuestMap = useCallback(() => {
    setIsShow(false);
    setGuestAddressModalKey(key => key + 1);
    setIsShowGuestMap(true);
  }, []);

  const toggleAddressPanel = useCallback(() => {
    setIsShow(prev => !prev);
  }, []);

  const onActiveClickHandler = useCallback(() => {
    if (!isUserLoggedIn) {
      openGuestMap();
      return;
    }
    const selectedAddress =
      isExpressHeading ||
      cartStore?.cart?.store_id === cartStore?.regionData?.default_store_id;
    toggleAddressPanel();
    setIsShowPickups(!selectedAddress);
  }, [
    cartStore?.cart?.store_id,
    cartStore?.regionData?.default_store_id,
    isExpressHeading,
    isUserLoggedIn,
    openGuestMap,
    toggleAddressPanel
  ]);

  let delivery = items[active]?.delivery;

  const onClickAddRegion = useCallback(() => {
    if (!isUserLoggedIn) {
      changeEditAddress(true);
    } else {
      if (isShowPickups) {
        setIsShow(false);
        setIsShowPPModal(true);
      } else {
        changeEditAddress(true);
      }
    }
  }, [changeEditAddress, isShowPickups, isUserLoggedIn]);

  const onChangeStore = useCallback(
    (param: boolean) => {
      if (!isLoading) {
        const selectedAddress = cartStore?.cart?.peopleAddress?.id;
        const isSwitchingToPickup = param && !isShowPickups;
        setIsShowPickups(param);
        if (isSwitchingToPickup) {
          showPriceChangeWarningNotification();
        }
        if (selectedAddress && !param) {
          onSelectAddress(cartStore?.cart?.peopleAddress)(false);
        }
      }
    },
    [cartStore?.cart?.peopleAddress, isLoading, isShowPickups, onSelectAddress]
  );

  const activeStore = pickupPointsList?.find(i => i.id === store_id);

  if (!isExpressHeading && !delivery && activeStore) {
    if (activeStore.type && expressPointsNames.includes(activeStore.type)) {
      delivery = 'Забрать сейчас';
    } else if (activeStore.nearestAvailableInterval) {
      delivery = createDeliveryTitle(activeStore);
    }
  }

  const isIncludesType = isExpressHeading
    ? false
    : activeStore
    ? !cartStore?.isCurrentAddressValidForExpress
    : expressPointsNames.includes(items?.[active]?.type || '');

  const isDeliveryStore =
    isExpressHeading || store_id === cartStore?.regionData?.default_store_id;
  const isExpressDelivery = cartStore?.isCurrentAddressValidForExpress;
  const nearestTypeD = nearestDelivery.find(
    (item: any) => item.type === 'delivery'
  );
  if (isDeliveryStore && nearestTypeD) {
    delivery = `${nearestTypeD?.title} с ${
      nearestTypeD.interval?.split('-')[0]
    }`;
  }

  const hasNoAddress =
    !isIncludesType &&
    (!selectedDaData || cartStore.isCartAddressFromOtherRegion);

  const heading = useMemo(() => {
    const addressTitle =
      selectedDaData && isDeliveryStore
        ? createAddressString({ dadata: cartStore?.cart?.dadata })
        : activeStore?.title || activeStore?.name || items[active]?.name || '';

    const chevron = (
      <Picture
        pictureClassName={`HeaderDropdown__chevron${
          isShow ? ' HeaderDropdown__chevron--rotate' : ''
        }`}
        icon={chevronDownWhiteOutlined}
        height='20'
        width='20'
      />
    );

    const isDesktopNoAddress = hasNoAddress && windowWidth >= 768;
    const deliveryTitle = hasNoAddress ? NO_ADDRESS_TITLE : addressTitle;
    const deliveryIcon =
      !hasNoAddress && isIncludesType ? storeWhiteFilled : carItalicWhiteFilled;

    return (
      <div
        onClick={onActiveClickHandler}
        className={`HeaderDropdown__active${
          hasNoAddress ? '' : ' HeaderDropdown__active--with-delivery'
        }`}
      >
        {isDesktopNoAddress ? (
          <>
            {!!delivery && (
              <div className='HeaderDropdown__delivery'>
                <div className='HeaderDropdown__delivery__time'>
                  <span className='HeaderDropdown__delivery__time__text'>
                    {delivery}
                  </span>
                </div>
              </div>
            )}
            <div className={'HeaderDropdown-clicker'}>{NO_ADDRESS_TITLE}</div>
          </>
        ) : (
          <div className='HeaderDropdown__delivery'>
            <div className='HeaderDropdown__delivery__address'>
              <Picture icon={deliveryIcon} height='16' width='16' />

              {!!deliveryTitle && (
                <span className='HeaderDropdown__delivery__address__title'>
                  {deliveryTitle}
                </span>
              )}
            </div>

            <div className='HeaderDropdown__delivery__time'>
              {!!delivery && (
                <span className='HeaderDropdown__delivery__time__text'>
                  {delivery}
                </span>
              )}
              {chevron}
            </div>

            {!hasNoAddress && isExpressDelivery && (
              <Tooltip
                isShowMobileNotification
                tooltipText={
                  <span
                    onClick={e => {
                      e.stopPropagation();
                    }}
                    className='block w-full text-sm text-center'
                  >
                    Вам доступна быстрая доставка от 30 мин. <br /> Только для
                    товаров со значком «ракета».
                  </span>
                }
                arrow
                positionImp={'bottom right'}
                offsetY={0}
                className='relative px-2 pt-2 pb-3'
              >
                <div className='bg-[#9645FF] p-[3px] flex items-center justify-center rounded'>
                  <Picture icon={rocketWhiteFilled} width={14} height={14} />
                </div>
              </Tooltip>
            )}
          </div>
        )}
      </div>
    );
  }, [
    active,
    activeStore?.name,
    activeStore?.title,
    cartStore?.cart?.dadata,
    delivery,
    hasNoAddress,
    isDeliveryStore,
    isExpressDelivery,
    isIncludesType,
    isShow,
    items,
    onActiveClickHandler,
    selectedDaData,
    windowWidth
  ]);

  if (!isLoadCart) {
    return (
      <LoadingSkeleton
        style={
          windowWidth >= 1280
            ? {
                height: '20px',
                width: '390px'
              }
            : windowWidth >= 768
            ? {
                height: '20px',
                width: '300px'
              }
            : {
                height: '20px',
                width: '100%'
              }
        }
      />
    );
  }

  return (
    <>
      <Suspense fallback={null}>
        <LazyExpressDeliveryInfoModal />
      </Suspense>
      <div ref={dropdownRef} className='HeaderDropdown'>
        {heading}
        {isChangeAddress && (
          <HeaderModalAddress
            changeModalState={hide}
            initialState={initialState}
            defState={defState}
          />
        )}

        {isShow && isUserLoggedIn && (
          <RegionModal
            onClose={hide}
            isShowPickups={isShowPickups}
            onChangeStore={onChangeStore}
            isLoading={isLoading}
            openedShops={openedShops}
            onClickAddRegion={onClickAddRegion}
            onSelectAddress={onSelectAddress}
            openPopupId={openPopupId}
            showOptionsPopup={showOptionsPopup}
            togglePopup={togglePopup}
            changeStateModalEdit={changeStateModalEdit}
            deleteAddress={deleteAddress}
            selectPP={selectPP}
            addressOptionsPopupRef={addressOptionsPopupRef}
            addressItemRefs={addressItemRefs}
          />
        )}

        {(isShowGuestMap || isShowPPModal) && (
          <ModalMapfunc
            key={guestAddressModalKey}
            changeModalState={hide}
            pickups={pickupPointsList}
            triggerChangePickupStore={triggerChangePickupStore}
            setShowModal={hide}
            activeStore={activeStore}
            isHideDelivery={isShowPPModal}
            isShouldAutoSwitchPickupStore={false}
          />
        )}
      </div>
    </>
  );
});

export default HeaderDropdown;
